import { useCallback } from 'react';
import { atom, useSetAtom } from 'jotai';

/**
 * Which control started the brief.
 *
 * Carried because the prompts compete: the same questionnaire is reachable from
 * the top of a browse page, from a module cycled into the results grid, from
 * the end of that grid, from the closing band on a detail page and from the
 * homepage. Without this they are one undifferentiated number, and there is no
 * way to tell a prompt that works from one that is simply shown often.
 *
 * `Unattributed` is not an entry point anyone can press. It is what the flow
 * falls back to if it is ever opened by writing the open atom directly, so an
 * unattributed brief shows up as its own series rather than being quietly
 * credited to whichever prompt happens to be first in the list.
 */
export type GetMatchedEntryPoint =
    | 'CategoryLead'
    | 'GridPrompt'
    | 'GridEndPrompt'
    | 'DetailBand'
    | 'HomeDiscover'
    | 'MyRequests'
    | 'Unattributed';

/** Which control opened the brief, or `null` while it is shut. */
export const getMatchedEntryPointAtom = atom<GetMatchedEntryPoint | null>(null);

/**
 * Whether the Get Matched questionnaire is up.
 *
 * One atom, because there is one surface. The brief used to sit behind a
 * desktop-only intro card, which is why opening it once forked on viewport
 * width and needed a guard to close the card if the window crossed the
 * breakpoint beneath it. The questionnaire takes the whole screen at every
 * width and opens the same way everywhere, so none of that applies.
 *
 * Derived from the entry point rather than held beside it, so the two cannot
 * disagree: open is exactly "we know which control started this", and closing
 * clears the attribution rather than leaving the last one behind for the next
 * brief to inherit.
 */
export const getMatchedModalOpenAtom = atom(
    (get) => get(getMatchedEntryPointAtom) !== null,
    (get, set, open: boolean) => {
        set(
            getMatchedEntryPointAtom,
            open ? (get(getMatchedEntryPointAtom) ?? 'Unattributed') : null,
        );
    },
);

/** Opens the brief, recording which control did it. */
export const useOpenGetMatchedModal = (entryPoint: GetMatchedEntryPoint) => {
    const setEntryPoint = useSetAtom(getMatchedEntryPointAtom);

    return useCallback(
        () => setEntryPoint(entryPoint),
        [setEntryPoint, entryPoint],
    );
};
